import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { LoginResponse, Me } from './models';

const STORAGE_KEY = 'sms.session';

/**
 * `localStorage` is absent during prerendering and in non-DOM test environments.
 * Reaching for it directly makes the service crash in both, so every access goes
 * through here and degrades to "no stored session" instead.
 */
const storage = {
  get(key: string): string | null {
    try {
      return globalThis.localStorage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      globalThis.localStorage?.setItem(key, value);
    } catch {
      // A session that cannot be persisted still works for this tab.
    }
  },
  remove(key: string): void {
    try {
      globalThis.localStorage?.removeItem(key);
    } catch {
      // Nothing to clean up.
    }
  },
};

interface StoredSession {
  token: string;
  expiresAtUtc: string;
  name: string;
  email: string;
  role: string;
}

/**
 * Holds the operator session. Permissions come from `/auth/me` and drive what the UI
 * offers — the server still enforces them, so this is presentation, not security.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly session = signal<StoredSession | null>(readStoredSession());
  private readonly me = signal<Me | null>(null);

  /** Renew once the token has less than this left, so a long form is never lost to expiry. */
  private static readonly RenewWithinMs = 10 * 60 * 1000;

  /** Shared so concurrent callers make one request rather than a burst. */
  private refreshing: Promise<boolean> | null = null;

  readonly isAuthenticated = computed(() => this.session() !== null);
  /** Empty until /auth/me has resolved — used to stop an operator acting on their own account. */
  readonly userId = computed(() => this.me()?.id ?? '');
  readonly displayName = computed(() => this.me()?.name ?? this.session()?.name ?? '');
  readonly email = computed(() => this.me()?.email ?? this.session()?.email ?? '');

  readonly role = computed(() => this.me()?.role ?? this.session()?.role ?? '');
  readonly permissions = computed(() => this.me()?.permissions ?? []);

  /** Whether this operator has two-factor set up, so the profile offers the right action. */
  readonly twoFactorEnabled = computed(() => this.me()?.twoFactorEnabled ?? false);

  get token(): string | null {
    return this.session()?.token ?? null;
  }

  /** True when the session carries the given `resource.action` permission. */
  can(permission: string): boolean {
    return this.permissions().includes(permission);
  }

  async login(email: string, password: string, code?: string): Promise<void> {
    const body: Record<string, string> = { email, password };
    if (code) {
      body['code'] = code;
    }

    const response = await firstValueFrom(
      this.http.post<LoginResponse>(`${environment.apiBaseUrl}/api/v1/auth/login`, body),
    );

    this.store(response);
    await this.loadMe();
  }

  private store(response: LoginResponse): void {
    const stored: StoredSession = {
      token: response.token,
      expiresAtUtc: response.expiresAtUtc,
      name: response.name,
      email: response.email,
      role: response.role,
    };

    storage.set(STORAGE_KEY, JSON.stringify(stored));
    this.session.set(stored);
  }

  /**
   * Renews the session before it lapses, so an operator mid-task is not dropped.
   *
   * Only worth doing while the token is still valid — the server requires an unexpired one —
   * and only when it is close enough to expiry to be worth a request. In flight, a single
   * promise is shared so several screens refreshing at once make one call, not several.
   */
  async refresh(): Promise<boolean> {
    const session = this.session();
    if (!session) {
      return false;
    }

    this.refreshing ??= this.performRefresh();

    try {
      return await this.refreshing;
    } finally {
      this.refreshing = null;
    }
  }

  /** True when the token is inside the renewal window and worth refreshing now. */
  needsRefresh(): boolean {
    const session = this.session();
    if (!session) {
      return false;
    }

    const remaining = new Date(session.expiresAtUtc).getTime() - Date.now();
    return remaining > 0 && remaining < AuthService.RenewWithinMs;
  }

  private async performRefresh(): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.post<LoginResponse>(`${environment.apiBaseUrl}/api/v1/auth/refresh`, {}),
      );

      this.store(response);

      // Permissions can change between renewals, so re-read them rather than assume.
      await this.loadMe();
      return true;
    } catch {
      // The absolute session ceiling was reached, or the account was deactivated. Either way
      // the session is over; dropping it is more honest than retrying forever.
      this.clear();
      return false;
    }
  }

  /** Refreshes identity + permissions. Safe to call on app start. */
  async loadMe(): Promise<void> {
    if (!this.session()) {
      return;
    }

    try {
      const me = await firstValueFrom(
        this.http.get<Me>(`${environment.apiBaseUrl}/api/v1/auth/me`),
      );
      this.me.set(me);
    } catch {
      // The token is gone or rejected — drop the session rather than sit in a half state.
      this.clear();
    }
  }

  logout(): void {
    this.clear();
    void this.router.navigate(['/login']);
  }

  clear(): void {
    storage.remove(STORAGE_KEY);
    this.session.set(null);
    this.me.set(null);
  }
}

function readStoredSession(): StoredSession | null {
  const raw = storage.get(STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as StoredSession;
    // Drop an expired token on read so the app never starts in a doomed state.
    if (new Date(parsed.expiresAtUtc).getTime() <= Date.now()) {
      storage.remove(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    storage.remove(STORAGE_KEY);
    return null;
  }
}
